"use client";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  HStack,
  Button,
  Text,
  Badge,
  Flex,
  IconButton,
  VStack, // Import VStack
} from "@chakra-ui/react";
import ActiveSwitch from "@/modules/Portaria/components/ActiveSwitch";
import { ReactElement, useEffect, useState } from "react";
import { AddIcon, EditIcon, MinusIcon } from "@chakra-ui/icons";

interface TableHeader {
  key: string;
  label: string;
  isVisible: boolean;
  width?: string;
  render?: (value: any, item: any) => ReactElement | string | number;
}

interface SlimTableProps {
  headers: TableHeader[];
  data: any[];
  searchValue?: string;
  onDelete?: (selectedData: any[]) => void;
  onAdd?: () => void;
  onEdit?: (selectedData: any) => void;
  onDataChange?: (newData: any[]) => void;
  showActions?: boolean;
  addButtonLabel?: string;
  deleteButtonLabel?: string;
  isReadSwitch?: boolean;
  children?: (selecteds: any) => ReactElement;
  isItemAll?: boolean;
  maxHeight?: string;
  onSelected?: any
}

const getNestedValue = (obj: any, path: string) => {
  const keys = path.split(".");
  return keys.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
    obj
  );
};

// Define fontSizePadrao for consistency with the table's overall font size
const fontSizePadrao = "2xs"; 

// Updated renderStatus function to respect image specifications
const renderStatus = (status: string | string[]) => {
  const statusList = Array.isArray(status) ? status : [status];

  return (
    <VStack align="start" spacing={1}>
      {statusList.map((s, i) => {
        const lower = String(s).toLowerCase(); // Ensure 's' is treated as a string
        let label: string;
        let color: string;
        let borderRadius: string = "md"; // Default to md (rounded rectangle)
        let minW: string = "28px"; // Default min-width for 2-char badges

        // Specific cases from the image, ordered by specificity/priority
        if (lower.includes("liberado doca")) {
          label = "LD";
          color = "rgba(17, 123, 170, 1)"; // Blue from image for LD
        } else if (lower.includes("entrada confirmada")) {
          label = "EC";
          color = "rgba(1, 152, 28, 1)"; // Green from image for EC
        } else if (lower.includes("chegada confirmada normal")) { // Explicitly for the "N" badge
          label = "N";
          color = "rgba(250, 188, 55, 1)"; // Orange from image for N
        } else if (lower.includes("chegada confirmada")) { // Default "Chegada Confirmada" (for CC badge)
          label = "CC";
          color = "rgba(250, 188, 55, 1)"; // Orange from image for CC
        } 
        // Other existing conditions, make sure their order doesn't conflict
        // These typically use 'full' borderRadius and smaller 'minW' if single char and perfectly round
        else if (lower.includes("livre")) { 
          label = "L";
          color = "rgba(1, 152, 28, 1)"; // Green
          borderRadius = "full";
          minW = "24px";
        } else if (lower.includes("ocupado")) { 
          label = "O";
          color = "rgba(229, 62, 62, 1)"; // Red
          borderRadius = "full";
          minW = "24px";
        } else if (lower.includes("urgente")) {
          label = "U";
          color = "rgba(229, 62, 62, 1)"; // Red
          borderRadius = "full";
          minW = "24px";
        } else if (lower.includes("baixa")) {
          label = "B";
          color = "rgba(1, 152, 28, 1)"; // Green
          borderRadius = "full";
          minW = "24px";
        } else if (lower.includes("aguar")) { // Renamed from 'aguar' or 'chegada' to avoid conflicts
          label = "AC";
          color = "rgba(229, 62, 62, 1)"; // Red
          borderRadius = "md"; // Assume md for now if not explicitly round
        } else {
          // Default case: if no specific match, get the first letter in uppercase
          label = String(s).charAt(0).toUpperCase();
          color = "gray"; // Default color if no match
          borderRadius = "md";
        }

        return (
          <HStack key={i} spacing={1} alignItems="center">
            <Badge
              borderRadius={'full'} 
              fontSize="10px" 
              px={1} 
              py={0.5}
              fontWeight="bold"
              bg={color}
              color="white"
              minW={'20px'}
              textAlign="center"
            >
              {label}
            </Badge>
            <Text fontSize={fontSizePadrao}>{s}</Text>
          </HStack>
        );
      })}
    </VStack>
  );
};


export default function SlimTable({
  headers,
  data,
  searchValue = "",
  onDelete,
  onAdd,
  onEdit,
  onDataChange,
  showActions = true,
  isReadSwitch,
  children,
  isItemAll,
  maxHeight = '500px',
  onSelected

}: SlimTableProps) {
  const [selectedRows, setSelectedRows] = useState<Set<number|any>>(new Set());
  const [tableData, setTableData] = useState(data);
  const [filteredData, setFilteredData] = useState(data);
  const [isDataUndefined, setIsDataUndefined] = useState<boolean>(
    data === undefined
  );

  useEffect(() => {
    // Check if data is undefined specifically
    setIsDataUndefined(data === undefined);
    const safeData = Array.isArray(data) ? data : [];
    setTableData(safeData);
    setFilteredData(safeData);
  }, [data]);

  useEffect(() => {
    if (searchValue.trim() === "") {
      setFilteredData(tableData);
    } else {
      const filtered = tableData.filter((item) => {
        return headers
          .filter((header) => header.isVisible && header.key !== "checkbox")
          .some((header) => {
            const value = getNestedValue(item, header.key);
            return (
              value !== null &&
              String(value).toLowerCase().includes(searchValue?.toLowerCase())
            );
          });
      });
      setFilteredData(filtered);
    }
  }, [searchValue, tableData, headers]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      if (isItemAll) {
        setSelectedRows(new Set(tableData.map((item) => item)));
      }
      setSelectedRows(new Set(tableData.map((item) => item.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (item: number | any, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      if (isItemAll) {
        newSelected.add(item);
      }
      newSelected.add(item?.id);
    } else {
      if(isItemAll) {
        newSelected.delete(item);
      }
      newSelected.delete(item?.id);
    }
    setSelectedRows(newSelected);
  };

  const handleSwitchChange = (id: number) => {
    const newData = tableData.map((item) =>
      item.id === id ? { ...item, ativo: !item.ativo } : item
    );
    setTableData(newData);
    onDataChange?.(newData);
  };

  const handleDeleteClick = () => {
    const selectedObjects = tableData.filter((row) => selectedRows.has(row.id));
    onDelete?.(selectedObjects);
  };

  const handleSelectedItens = () => {
    const selectedObjects = tableData.filter((row) => selectedRows.has(row.id));
    onSelected?.(selectedObjects);
  };

  const getStatusColorScheme = (status: string) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === "pendente") return "gray";
    if (statusLower === "armazenado") return "green.500"; // green
    if (statusLower === "conferido") return "#117baa"; // blue
    if (statusLower === "divergente") return "red.600"; // red
    if (statusLower.includes("em")) return "orange";
    if (statusLower.endsWith("do")) return "green.500"; // verde
    return "gray";
  };

  // Cores harmoniosas com #0f4f6d
  const getOccupationBadgeProps = (value: boolean) => {
    if (value === true) {
      return {
        bg: "#0f8599", // Um azul turquesa - harmonioso com o azul principal
        color: "white",
        text: "Sim",
      };
    } else {
      return {
        bg: "#6d7a81", // Um cinza azulado - harmonioso com o azul principal
        color: "white",
        text: "Não",

      };
    }
  };
  useEffect(() => {
    if (onSelected) {
      const selectedObjects = tableData.filter((row) => selectedRows.has(row.id));
      onSelected(selectedObjects);
    }
  }, [selectedRows, tableData]);

  const renderCellContent = (item: any, header: TableHeader) => {
    const headerKey = header.key;
    
    // Se o header tem uma função render customizada, usa ela
    if (header.render) {
      const value = getNestedValue(item, headerKey);
      return header.render(value, item);
    }

    // Caso contrário, usa a renderização padrão
    if (headerKey === "checkbox") {
      return (
        <Checkbox
          size="sm"
          isChecked={selectedRows.has(isItemAll ? item : item.id)}
          onChange={(e) => handleSelectRow(item, e.target.checked)}
        />
      );
    } else if (headerKey === "ativo") {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={0}>
          <ActiveSwitch
            size="sm"
            isChecked={item.ativo}
            onChange={() => handleSwitchChange(item.id)}
            isReadOnly={isReadSwitch}
          />
        </Box>
      );
    } else if (typeof getNestedValue(item, headerKey) === "boolean") {
      const value = getNestedValue(item, headerKey);
      if (value === null) return "-";

      const badgeProps = getOccupationBadgeProps(value);

      return (
        <Badge
          borderRadius="full"
          px={1}
          py={1}
          bg={badgeProps.bg}
          color={badgeProps.color}
          textTransform="capitalize"
          fontWeight="medium"
        >
          {badgeProps.text}
        </Badge>
      );
    } else if (headerKey === "status") { // Changed from 'situacao' to 'status'
      const value = getNestedValue(item, headerKey);
      if (value === null) return "-";

      return renderStatus(value); // Use your renderStatus function here
    } else {
      const value = getNestedValue(item, headerKey);
      return value !== null ? value : "-";
    }
  };

  return (
    <Box fontSize="2xs"> {/* Apply font size here to affect all text */}
      {showActions && (
        <Flex p={2} justifyContent="flex-end" alignItems="center">
          <HStack>
            <IconButton
              aria-label="Add"
              icon={<AddIcon />}
              onClick={onAdd}
              colorScheme="green"
              size="sm"
              borderRadius="full"
            />
            <IconButton
              aria-label="Remove"
              icon={<MinusIcon />}
              onClick={handleDeleteClick}
              colorScheme="red"
              size="sm"
              borderRadius="full"
            />
            <IconButton
              aria-label="Edit"
              icon={<EditIcon />}
              onClick={() => {
                if (selectedRows.size === 1) {
                  const [selectedId] = Array.from(selectedRows);
                  onEdit?.(selectedId);
                }
              }}
              colorScheme="blue"
              size="sm"
              borderRadius="full"
            />
          </HStack>
        </Flex>
      )}

      {!isDataUndefined && (
        <Box
          overflow="auto"
          maxHeight={maxHeight}
          border="1px solid"
          borderColor="gray.300"
        >
          <Table variant="striped" size="sm">
            <Thead position={"sticky"} top={0} zIndex={1}>
              <Tr>
                {headers
                  .filter((header) => header.isVisible)
                  .map((header, index) => (
                    <Th
                      key={index}
                      color="white"
                      bg="#0f4f6d" // Dark blue for header background
                      textAlign="center"
                      width={header.width}
                      px={1} // Added horizontal padding for spacing between headers
                      py={1} // Vertical padding for headers
                      borderRight="1px solid #1a6d92" // Separator line for headers
                      _last={{ borderRight: "none" }} // No separator on the last header
                    >
                      {header.key === "checkbox" ? (
                        <Checkbox
                          size="sm"
                          isChecked={
                            selectedRows.size === tableData.length &&
                            tableData.length > 0
                          }
                          isIndeterminate={
                            selectedRows.size > 0 &&
                            selectedRows.size < tableData.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      ) : (
                        <Text fontSize={'2xs'}>{header.label}</Text> // Ensure header label is 2xs
                      )}
                    </Th>
                  ))}
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <Tr
                    key={item.id}
                    // Apply alternating row colors for "light blue" effect
                    _odd={{ bg: "gray.50" }} 
                    _even={{ bg: "white" }}
                    bg={selectedRows.has(item.id) ? "blue.50" : undefined} // Keep selected row highlight if desired
                  >
                    {headers
                      .filter((header) => header.isVisible)
                      .map((header, idx) => (
                        <Td
                          key={`${item.id}-${header.key}-${idx}`}
                          onDoubleClick={() => onEdit?.(item.id)}
                          textAlign="center"
                          fontSize={"2xs"} // Ensure cell content is 2xs
                          px={1} // Added horizontal padding for spacing within cells
                          py={1} // Vertical padding for cells
                        >
                          {renderCellContent(item, header)}
                        </Td>
                      ))}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td
                    colSpan={headers.filter((h) => h.isVisible).length}
                    textAlign="center"
                    fontSize={"2xs"} // Ensure "Nenhum registro encontrado" is 2xs
                  >
                    Nenhum registro encontrado
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
      {isDataUndefined && (
        <Box textAlign="center" mt={4}>
          <Text color="red.500" fontSize={"2xs"}>Nenhum dado encontrado.</Text>
        </Box>
      )}

      {children?.(selectedRows || [])}
    </Box>
  );
}